import styled from '@emotion/styled';

import { Badge } from '@/components/common/Badge';
import { screen } from '@/components/common/breakpoints';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { Card } from '@/components/common/Card';
import { HeadingLevelProvider } from '@/components/common/Heading/context';
import { Image } from '@/components/common/image';
import { Paragraph } from '@/components/common/MarkUp';
import { Section } from '@/components/layout/Section';
import { getTextColour } from '@/lib/colourUtils';

import type {
  BackgroundColourTokenType,
  TextColourTokenType,
} from '@/types/colours';
import type { SanityQuoteModuleType } from '@/types/sanity';

export interface QuoteModuleProps extends SanityQuoteModuleType {}

export const QuoteModule = ({
  tag,
  heading,
  headline,
  quote,
  image,
  contentAlign,
  backgroundColour,
  blockSpacing,
  paddingAdjustment,
}: QuoteModuleProps) => {
  const backgroundColourToken = backgroundColour?.token;
  return (
    <Section
      verticalPadding={paddingAdjustment ?? true}
      spacing={blockSpacing}
      backgroundColour={backgroundColourToken}
      className="quote-module"
    >
      <StyledSection>
        <HeadingSection color={getTextColour(backgroundColourToken)}>
          {tag?.tagText && (
            <Badge
              bgColour={tag?.tagColour}
              margin="0 0 var(--spacing-xxx-small) 0"
            >
              {tag.tagText}
            </Badge>
          )}
          <HeadingLevelProvider level={2}>
            <CaptionHeadingDescription
              caption={headline}
              heading={heading}
              desktopAlignment="center"
              mobileAlignment="center"
              padding={{ caption: 'x-small', captionMobile: 'x-small' }}
            />
          </HeadingLevelProvider>
        </HeadingSection>
        <ContentContainer alignment={contentAlign}>
          <QuoteContainer borderRadius="s" alignment={contentAlign}>
            <Quote size="body-lg">{quote?.text}</Quote>
            <Paragraph size="heading-sm">{quote?.author}</Paragraph>
            <Role size="body-lg">{quote?.role}</Role>
          </QuoteContainer>
          <ImageWrapper backgroundColour={image?.backgroundColour}>
            <Image
              {...image}
              objectFit="cover"
              objectPosition={image.position}
              radius="l"
            />
          </ImageWrapper>
        </ContentContainer>
      </StyledSection>
    </Section>
  );
};

const StyledSection = styled.section`
  margin-top: var(--spacing-500);
  margin-bottom: var(--spacing-500);
  padding: var(--spacing-500) 0;
`;

const Quote = styled(Paragraph)`
  margin-bottom: var(--spacing-x-small);
`;

const Role = styled(Paragraph)`
  color: var(--decorative-soft-galaxy);
`;

const HeadingSection = styled.div<{
  color: TextColourTokenType;
}>`
  text-align: center;
  margin-bottom: var(--spacing-500);
  color: ${({ color }) => `var(${color})`};
  p {
    color: ${({ color }) => `var(${color})`};
  }

  ${screen.sm} {
    margin-bottom: var(--spacing-800);
  }
`;

const QuoteContainer = styled(Card)<{ alignment: 'left' | 'right' }>`
  text-align: center;
  background-color: var(--background-subtle);
  border-radius: 12px;
  width: 100%;
  margin-bottom: var(--spacing-small);

  ${screen.sm} {
    position: absolute;
    left: ${({ alignment }) => (alignment === 'left' ? '0' : 'initial')};
    right: ${({ alignment }) => (alignment === 'left' ? 'initial' : '0')};
    top: var(--spacing-500);
    z-index: 1;
    width: 45%;
  }
`;

const ContentContainer = styled.div<{ alignment: 'left' | 'right' }>`
  ${screen.sm} {
    display: flex;
    position: relative;
    justify-content: ${({ alignment }) =>
      alignment === 'left' ? 'flex-end' : 'flex-start'};
  }
`;

const ImageWrapper = styled.div<{
  backgroundColour: BackgroundColourTokenType;
}>`
  div {
    height: 400px;
    width: 100%;
    border-radius: 12px;
    background-color: ${({ backgroundColour }) => `var(${backgroundColour})`};
  }

  ${screen.sm} {
    width: 58%;
  }
`;
